import {
	Button,
	CircularProgress,
	Grid2,
	Snackbar,
	SnackbarCloseReason,
	TextField,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { ChangeEvent, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import MesfModal from "../../../../../components/modals/modal.mesf";
import { Alert } from "../../../../../controls/alert";
import { HelmetDexteel } from "../../../../../helmet/HelmetDexteel";
import { Action } from "../../models/Action";
import { INITIAL_VALUES } from "../../models/FormInitialValues";
import { Profile } from "../../models/Profile";
import {
	getActionsByProfileId,
	upsertProfile,
	upsertProfileActions,
} from "../../repositories/ProfilesRepository";
import { DataTable } from "./ProceduresDataTable";

export const searchActionsOfProfile = ({
	profileId,
}: {
	profileId: number | null;
}) => {
	return useQuery<Action[], Error>({
		queryKey: ["actions", profileId],
		queryFn: ({ signal }) => {
			if (profileId) {
				return getActionsByProfileId({ profileId }, signal);
			} else {
				return [];
			}
		},
	});
};

type Props = {
	profileForEdit: Profile | null;
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	suffixTitle?: string;
};

export const ActionsOfProfile = ({
	profileForEdit,
	show,
	onHide,
	suffixTitle,
}: Props) => {
	const [showNameSnackbar, setShowNameSnackbar] = useState(false);
	const [showActionsSnackbar, setShowActionsSnackbar] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [actionsSelected, setActionsSelected] = useState<Action[]>([]);

	const [error, setError] = useState<string>("");

	const [profileName, setProfileName] = useState<string>("");
	const [nameError, setNameError] = useState<string>("");
	const [showEditInput, setShowEditInput] = useState<boolean>(false);

	const {
		data: actionsOfProfile,
		isLoading,
		isError,
		error: e,
	} = searchActionsOfProfile({ profileId: profileForEdit?.ProfileId ?? null });

	const { handleSubmit } = useForm<Profile>({ defaultValues: INITIAL_VALUES });

	const handleClose = (
		event?: React.SyntheticEvent<any> | Event,
		reason?: SnackbarCloseReason,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setShowActionsSnackbar(false);
		setShowNameSnackbar(false);
	};

	const handleChangeProfielName = (ev: any) => {
		setProfileName(ev.target.value);
	};

	const queryClient = useQueryClient();
	const editProfile = useMutation({
		mutationFn: upsertProfile,
		onSuccess: () => {
			onHide(true);
			setShowNameSnackbar(true);
		},
		onError: (error: Error) => {
			setNameError(error.message);
		},
		onSettled: () => {
			setIsSubmitLoading(false);
		},
	});

	const editProfileActions = useMutation({
		mutationFn: upsertProfileActions,
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: ["actions", profileForEdit?.ProfileId],
			});

			if (profileForEdit?.ProfileName !== profileName) {
				await editProfile.mutate({
					ProfileId: profileForEdit?.ProfileId!,
					ProfileName: profileName,
				});
				setTimeout(() => setShowActionsSnackbar(true), 2600);
			} else {
				setShowActionsSnackbar(true);
				onHide(true);
			}
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => {
			setIsSubmitLoading(false);
		},
	});

	const onSubmit: SubmitHandler<Profile> = async () => {
		setIsSubmitLoading(true);
		const sameActions =
			actionsOfProfile?.length === actionsSelected.length &&
			actionsOfProfile.every(
				(action, index) => action.ActionId === actionsSelected[index].ActionId,
			);
		if (sameActions) {
			await editProfile.mutate({
				ProfileId: profileForEdit?.ProfileId!,
				ProfileName: profileName,
			});
		} else {
			await editProfileActions.mutate({
				profileId: profileForEdit?.ProfileId!,
				actions: actionsSelected,
			});
		}
	};

	useEffect(() => {
		if (show) {
			setShowEditInput(false);
			setProfileName(profileForEdit?.ProfileName as string);
			setIsSubmitLoading(false);
		}
	}, [show, profileForEdit?.ProfileId]);

	useEffect(() => {
		if (e && isError) {
			setError(e.message);
		}
	}, [e, isError]);

	return (
		<>
			{show && (
				<HelmetDexteel
					title={`Edit Profile${suffixTitle ? ` - ${suffixTitle}` : ""}`}
				/>
			)}
			<MesfModal
				title="EDIT PROFILE"
				open={show}
				handleClose={() => onHide(false)}
				id="edit-profile-modal"
				maxWidth="xl"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content style={{ padding: "0 30px 15px" }}>
						<Grid2
							container
							sx={{
								position: "relative",

								"& .MuiFormLabel-asterisk": {
									display: "none",
								},
							}}
						>
							<Grid2 size={{ md: 12, xs: 12 }}>
								<TextField
									id="outlined-basic"
									label="Name"
									variant="outlined"
									value={profileName}
									fullWidth
									size="small"
									margin="dense"
									autoComplete="off"
									onChange={(e: ChangeEvent) => handleChangeProfielName(e)}
									InputLabelProps={{
										shrink: true,
									}}
									required={true}
									error={profileName.length === 0}
								/>
							</Grid2>
						</Grid2>
						{isLoading && (
							<Grid2
								container
								justifyContent="center"
								alignItems="center"
								style={{ minHeight: "60px" }}
							>
								<CircularProgress size="3rem" />
							</Grid2>
						)}
						{!isLoading && actionsOfProfile && (
							<DataTable
								actionsOfProfile={actionsOfProfile}
								onSelectionChanged={setActionsSelected}
							/>
						)}
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						{!isLoading && (
							<Grid2 container spacing={2} justifyContent="flex-end">
								<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
									<Button
										fullWidth
										variant="contained"
										color="inherit"
										onClick={() => onHide(false)}
									>
										Cancel
									</Button>
								</Grid2>
								<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
									<Button
										fullWidth
										startIcon={
											isSubmitLoading && <CircularProgress size="1rem" />
										}
										disabled={isSubmitLoading}
										variant="contained"
										color="primary"
										type="submit"
									>
										Save
									</Button>
								</Grid2>
							</Grid2>
						)}
					</MesfModal.Actions>
				</form>
			</MesfModal>

			<Snackbar
				open={showActionsSnackbar}
				autoHideDuration={2500}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="info" onClose={handleClose}>
					The actions of the profile were edited successfully
				</Alert>
			</Snackbar>
			<Snackbar
				open={showNameSnackbar}
				autoHideDuration={2500}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="success" onClose={handleClose}>
					The name of the profile were edited successfully
				</Alert>
			</Snackbar>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error Editing Profile"
			/>
			<ErrorModal
				error={nameError}
				onHide={() => setNameError("")}
				title="Error Editing Profile"
			/>
		</>
	);
};
